import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { HiArrowLeft, HiOutlineDuplicate, HiOutlineHashtag } from 'react-icons/hi';
import { FaEthereum, FaWallet } from 'react-icons/fa';
import { TbBrandBinance, TbWallet } from 'react-icons/tb';
import { MdOutlineAccountBalanceWallet } from 'react-icons/md';
import { RiSafe2Line, RiShieldKeyholeLine } from 'react-icons/ri';
import { useWallet, readStoredUpline, isWalletAddress } from '../context/WalletContext';
import { useToast } from '../context/ToastContext';
import { api } from '../services/api';
import { detectWalletAvailability, sendBnb, shortAddress } from '../services/wallet';
import Modal from './Modal';
import './ConnectModal.css';

async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

function formatBnb(value) {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return '0';
  return amount.toLocaleString(undefined, { maximumFractionDigits: 8 });
}

function registerWalletError(err) {
  const text = String(err?.message || '');
  const cancelled =
    err?.code === 4001 ||
    /user rejected|user denied|cancelled|denied transaction/i.test(text);
  if (cancelled) {
    const out = new Error('Registration cancelled.');
    out.code = 4001;
    return out;
  }
  if (/network fee/i.test(text)) {
    return new Error('Your wallet needs a little BNB for the network.');
  }
  if (/does not have enough/i.test(text)) {
    return new Error('Your wallet does not have enough BNB to register.');
  }
  if (/payment|could not be sent/i.test(text)) {
    return new Error('Registration could not be completed. Check your BNB balance and try again.');
  }
  return err;
}

const WALLETS = [
  {
    id: 'metamask',
    name: 'MetaMask',
    hint: 'Browser extension & app',
    Icon: FaEthereum,
    color: '#F6851B',
  },
  {
    id: 'trust',
    name: 'Trust Wallet',
    hint: 'Mobile & browser',
    Icon: RiShieldKeyholeLine,
    color: '#0500FF',
  },
  {
    id: 'binance',
    name: 'Binance Wallet',
    hint: 'BNB Chain wallet',
    Icon: TbBrandBinance,
    color: '#F0B90B',
  },
  {
    id: 'coinbase',
    name: 'Coinbase Wallet',
    hint: 'Extension & app',
    Icon: MdOutlineAccountBalanceWallet,
    color: '#0052FF',
  },
  {
    id: 'okx',
    name: 'OKX Wallet',
    hint: 'Web3 multi-chain',
    Icon: FaWallet,
    color: '#FFFFFF',
  },
  {
    id: 'bitget',
    name: 'Bitget Wallet',
    hint: 'Formerly BitKeep',
    Icon: TbWallet,
    color: '#00F0FF',
  },
  {
    id: 'tokenpocket',
    name: 'TokenPocket',
    hint: 'Multi-chain wallet',
    Icon: FaWallet,
    color: '#2980FE',
  },
  {
    id: 'safepal',
    name: 'SafePal',
    hint: 'Hardware & software',
    Icon: RiSafe2Line,
    color: '#4A21EF',
  },
  {
    id: 'injected',
    name: 'Browser Wallet',
    hint: 'Any injected provider',
    Icon: FaWallet,
    color: '#00D2EF',
  },
];

export default function ConnectModal() {
  const {
    connectOpen,
    connectReason,
    connectMode,
    setConnectMode,
    pendingWalletId,
    setPendingWalletId,
    closeConnect,
    connectAndAuth,
    register,
    referralUpline,
    setReferralUpline,
    setError,
    error,
    config,
    address,
  } = useWallet();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [selectedWallet, setSelectedWallet] = useState(pendingWalletId || 'injected');
  const [upline, setUpline] = useState('');
  const [localError, setLocalError] = useState('');
  const [availability, setAvailability] = useState({});
  const [payStep, setPayStep] = useState('transfer');
  const [manualHash, setManualHash] = useState('');
  const autoRegisterKeyRef = useRef('');
  const navigate = useNavigate();

  const registerFee = Number(config?.register_fee_bnb);
  const registerWallet = String(config?.register_wallet || '').trim();
  const feeRequired = Number.isFinite(registerFee) && registerFee > 0;

  useEffect(() => {
    if (!connectOpen) {
      setLocalError('');
      setBusy(false);
      autoRegisterKeyRef.current = '';
      return;
    }
    setAvailability(detectWalletAvailability());
    setSelectedWallet(pendingWalletId || 'injected');
    if (connectMode === 'register') {
      setUpline(referralUpline || readStoredUpline() || '');
      setPayStep('transfer');
      setManualHash('');
    }
    if (error && connectMode === 'picker') {
      setLocalError(error);
      toast.error(error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectOpen, connectMode]);

  async function finishRegister(txHash = '', referOverride = '', knownWallet = '') {
    const refer = (referOverride || upline).trim();
    await register(refer, selectedWallet || pendingWalletId || 'injected', txHash, knownWallet);
    toast.success('Registration complete');
    navigate('/dashboard');
  }

  async function ensureReferReady(referValue = upline, knownWallet = '') {
    const refer = String(referValue || '').trim();
    if (!isWalletAddress(refer)) {
      const err = new Error('Enter a valid refer wallet address (0x…)');
      err.code = 'INVALID_UPLINE';
      throw err;
    }
    const wallet = String(knownWallet || address || '').trim();
    if (!isWalletAddress(wallet)) {
      throw new Error('Connect your wallet to continue');
    }
    if (refer.toLowerCase() === wallet.toLowerCase()) {
      const err = new Error('Upline cannot be your own wallet');
      err.code = 'INVALID_UPLINE';
      throw err;
    }
    await api.checkRegister(wallet, refer);
    setReferralUpline(refer);
    setUpline(refer);
    return refer;
  }

  function returnToRefer(message) {
    setPayStep('transfer');
    setConnectMode('register');
    if (message) {
      setLocalError(message);
      setError(message);
      toast.error(message);
    }
  }

  /** One-shot: validate refer → optional BNB send → create account. */
  async function completeRegistration(referValue, { showFallback = true, knownWallet = '' } = {}) {
    if (feeRequired && !isWalletAddress(registerWallet)) {
      const msg = 'Registration wallet is not set. Ask admin to set it.';
      setLocalError(msg);
      toast.error(msg);
      setConnectMode('register');
      return;
    }

    setLocalError('');
    setBusy(true);
    try {
      const refer = await ensureReferReady(referValue, knownWallet);
      if (!feeRequired) {
        await finishRegister('', refer, knownWallet);
        return;
      }

      setConnectMode('pay');
      setPayStep('approve');
      const { txHash } = await sendBnb({
        to: registerWallet,
        amount: registerFee,
        onSubmitted: () => setPayStep('confirming'),
      });
      setPayStep('registering');
      await finishRegister(txHash, refer, knownWallet);
    } catch (err) {
      if (err.code === 'INVALID_UPLINE' || /refer|upline/i.test(err.message || '')) {
        returnToRefer(err.message);
        return;
      }
      const friendly = registerWalletError(err);
      if (showFallback && feeRequired) {
        setConnectMode('pay');
        setPayStep('transfer');
      } else {
        setConnectMode('register');
      }
      setLocalError(friendly.message);
      setError(friendly.message);
      if (friendly.code === 4001) toast.info(friendly.message);
      else toast.error(friendly.message || 'Registration failed');
    } finally {
      setBusy(false);
    }
  }

  async function runConnect(walletId) {
    setBusy(true);
    setLocalError('');
    setSelectedWallet(walletId);
    setPendingWalletId(walletId);
    try {
      const result = await connectAndAuth(walletId);
      if (result.status === 'needs_register') {
        const refer = referralUpline || readStoredUpline() || '';
        setUpline(refer);
        if (isWalletAddress(refer)) {
          // Invite link already has refer — finish without another confirm screen.
          autoRegisterKeyRef.current = `${String(result.wallet).toLowerCase()}:${refer.toLowerCase()}`;
          await completeRegistration(refer, {
            showFallback: true,
            knownWallet: result.wallet,
          });
        } else {
          setConnectMode('register');
        }
      } else {
        toast.success('Wallet connected');
        navigate('/dashboard');
      }
    } catch (e) {
      setLocalError(e.message);
      setError(e.message);
      toast.error(e.message || 'Connection failed');
      setConnectMode('picker');
    } finally {
      setBusy(false);
    }
  }

  async function onPickWallet(walletId) {
    await runConnect(walletId);
  }

  async function onRegister(e) {
    e.preventDefault();
    await completeRegistration(upline.trim());
  }

  async function payWithWallet() {
    await completeRegistration(upline.trim());
  }

  async function submitManualHash(event) {
    event.preventDefault();
    const hash = manualHash.trim();
    if (!/^0x[a-fA-F0-9]{64}$/.test(hash)) {
      const msg = 'Enter the 66-character transaction hash that starts with 0x.';
      setLocalError(msg);
      toast.error(msg);
      return;
    }
    setBusy(true);
    setLocalError('');
    try {
      const refer = await ensureReferReady(upline.trim());
      setPayStep('registering');
      await finishRegister(hash, refer);
    } catch (err) {
      if (err.code === 'INVALID_UPLINE' || /refer|upline/i.test(err.message || '')) {
        returnToRefer(err.message);
        return;
      }
      setPayStep('transfer');
      setLocalError(err.message);
      setError(err.message);
      toast.error(err.message || 'Registration failed');
    } finally {
      setBusy(false);
    }
  }

  // In-app / invite path: wallet already connected and refer known → finish immediately.
  useEffect(() => {
    if (!connectOpen || connectMode !== 'register' || busy) return;
    const refer = referralUpline || readStoredUpline() || '';
    const wallet = String(address || '').trim();
    if (!isWalletAddress(refer) || !isWalletAddress(wallet)) return;
    const key = `${wallet.toLowerCase()}:${refer.toLowerCase()}`;
    if (autoRegisterKeyRef.current === key) return;
    autoRegisterKeyRef.current = key;
    void completeRegistration(refer, { knownWallet: wallet });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectOpen, connectMode, address, referralUpline]);

  const hashLooksValid = /^0x[a-fA-F0-9]{64}$/.test(manualHash);
  const referReady = isWalletAddress(upline);
  const paying = connectMode === 'pay';
  const title = paying || connectMode === 'register' ? 'Register' : 'Choose a wallet';
  const subtitle = paying
    ? payStep === 'transfer'
      ? `Retry ${formatBnb(registerFee)} BNB registration payment.`
      : 'Finishing registration…'
    : connectMode === 'register'
      ? 'Enter your refer wallet to create this account.'
      : connectReason ||
        'Popular dApp wallets on BNB Smart Chain. Detected wallets are marked ready.';

  const footer = paying ? (
    payStep === 'transfer' ? (
      <>
        <button className="btn btn--primary" type="button" onClick={payWithWallet} disabled={busy || !referReady}>
          <TbWallet />
          Pay & Register
        </button>
        <button
          className="btn btn--ghost"
          type="button"
          onClick={() => {
            setConnectMode('register');
            setLocalError('');
          }}
          disabled={busy}
        >
          <HiArrowLeft /> Back
        </button>
      </>
    ) : null
  ) : connectMode === 'register' ? (
    <button
      className="btn btn--ghost"
      type="button"
      onClick={() => {
        setConnectMode('picker');
        setLocalError('');
      }}
      disabled={busy}
    >
      <HiArrowLeft /> Back to wallets
    </button>
  ) : null;

  return (
    <Modal
      open={connectOpen}
      onClose={busy || payStep === 'approve' || payStep === 'confirming' || payStep === 'registering' ? undefined : closeConnect}
      eyebrow="Wallet Connect"
      title={title}
      subtitle={subtitle}
      footer={footer}
    >
      {connectMode === 'picker' ? (
        <div className="wallet-list">
          {WALLETS.map((wallet, index) => {
            const Icon = wallet.Icon;
            const detected =
              !!availability[wallet.id] || (wallet.id === 'injected' && availability.injected);
            return (
              <motion.button
                key={wallet.id}
                type="button"
                className="wallet-item"
                disabled={busy}
                onClick={() => onPickWallet(wallet.id)}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.04 }}
                whileHover={{ scale: 1.02, x: 2 }}
                whileTap={{ scale: 0.98 }}
              >
                <span className="wallet-item__icon" style={{ color: wallet.color }}>
                  <Icon size={22} />
                </span>
                <span className="wallet-item__meta">
                  <strong>{wallet.name}</strong>
                  <small>{wallet.hint}</small>
                </span>
                <span className={`wallet-item__status${detected ? ' is-on' : ''}`}>
                  {detected ? 'Detected' : 'Install'}
                </span>
              </motion.button>
            );
          })}
          {busy ? <p className="modal__busy">Connecting…</p> : null}
          {localError ? <p className="modal__error">{localError}</p> : null}
        </div>
      ) : null}

      {connectMode === 'register' ? (
        busy && referReady ? (
          <div className="modal__connecting">
            <div className="modal__spinner" />
            <p className="modal__busy">
              {feeRequired ? 'Confirm the BNB fee in your wallet…' : 'Creating your account…'}
            </p>
          </div>
        ) : (
          <form className="modal__form" onSubmit={onRegister}>
            <label>
              Refer wallet address
              <input
                value={upline}
                onChange={(e) => setUpline(e.target.value)}
                placeholder="0x…"
                required
                spellCheck={false}
                autoComplete="off"
              />
            </label>
            <button className="btn btn--primary" type="submit" disabled={busy || !referReady}>
              {busy ? 'Registering…' : feeRequired ? `Register (${formatBnb(registerFee)} BNB)` : 'Register'}
            </button>
            {localError ? <p className="modal__error">{localError}</p> : null}
          </form>
        )
      ) : null}

      {connectMode === 'pay' ? (
        payStep === 'transfer' ? (
          <div className="stake-transfer">
            <div className="stake-preview">
              <div>
                <span>Registration</span>
                <strong>{formatBnb(registerFee)} BNB</strong>
              </div>
              <div>
                <span>From</span>
                <strong>{shortAddress(address) || 'Connected wallet'}</strong>
              </div>
            </div>
            <label className="stake-transfer__label" htmlFor="register-fee-wallet">To</label>
            <div className="stake-transfer__address">
              <code id="register-fee-wallet">{registerWallet}</code>
              <button
                type="button"
                aria-label="Copy registration wallet"
                onClick={() => copyText(registerWallet).then((ok) => ok && toast.success('Address copied'))}
              >
                <HiOutlineDuplicate />
              </button>
            </div>
            <form className="register-hash" onSubmit={submitManualHash}>
              <div className="register-hash__head">
                <span className="register-hash__ico" aria-hidden="true">
                  <HiOutlineHashtag />
                </span>
                <div>
                  <label htmlFor="register-tx-hash">Transaction hash</label>
                  <p>Already paid? Paste the hash to finish.</p>
                </div>
              </div>
              <div className={`register-hash__box${hashLooksValid ? ' is-ready' : ''}`}>
                <input
                  id="register-tx-hash"
                  value={manualHash}
                  onChange={(event) => setManualHash(event.target.value.trim())}
                  placeholder="0x…"
                  autoComplete="off"
                  spellCheck={false}
                  inputMode="text"
                  aria-invalid={manualHash ? !hashLooksValid : undefined}
                />
                <button
                  type="submit"
                  className="register-hash__btn"
                  disabled={busy || !hashLooksValid || !referReady}
                >
                  Verify
                </button>
              </div>
            </form>
            {localError ? <p className="modal__error">{localError}</p> : null}
          </div>
        ) : (
          <div className="modal__connecting">
            <div className="modal__spinner" />
            <p className="modal__busy">
              {payStep === 'approve'
                ? 'Confirm in your wallet…'
                : payStep === 'confirming'
                  ? 'Confirming payment…'
                  : 'Creating your account…'}
            </p>
          </div>
        )
      ) : null}
    </Modal>
  );
}
